import { useManager } from 'dreamstate';
import { useCallback, useMemo } from 'react';

import { AnyObject, Optional } from '@vimeo/core/types/generic';
import { InteractionSessionManager } from '@vimeo/live-core/store/interaction/session';
import { EApiErrorCode } from '@vimeo/live-core/types/error';
import {
  EInteractionAuthOption,
  IAuthorizeInteractionUserParams,
  IInteractionRoomInfo,
} from '@vimeo/live-core/types/interaction_room';
import { isApiError } from '@vimeo/live-core/utils/error';
import { Logger, useLogger } from '@vimeo/logging';

import { translations } from '../../../../../../translations';

type TOnLoginParams = Pick<
  IAuthorizeInteractionUserParams,
  'roomDisplayName' | 'roomGuestCode' | 'roomEmail' | 'roomRegistrantUuid'
>;

export interface IUserLoginProps {
  isLoading: boolean;
  isLoginFailed: boolean;
  isRoomUserAuthAttempted: boolean;
  roomInfo: Optional<IInteractionRoomInfo>;
  log: Logger;
  error: Optional<AnyObject>;
  errorMessage: Optional<string>;
  onLogin: (params?: TOnLoginParams) => Promise<void>;
  onResetError: () => void;
}

/**
 * Generic hook to process and store auth in isolated forms.
 */
export function useInteractionLoginOption(loginType: EInteractionAuthOption): IUserLoginProps {
  const log: Logger = useLogger('auth');
  const {
    roomInfo: { value: roomInfo },
    roomUser: { error, isLoading },
    isRoomUserAuthAttempted,
    interactionSessionActions,
  } = useManager(InteractionSessionManager);

  const isLoginFailed: boolean = Boolean(error) && error?.data?.authType === loginType;

  /**
   * Map API error to translated error message.
   */
  const errorMessage: Optional<string> = useMemo(() => {
    if (!isLoginFailed || isLoading) {
      return null;
    }

    if (isApiError(error)) {
      switch (error.data.body.errorCode) {
        case EApiErrorCode.ROOM_AUTHORIZATION_FAILED:
          return translations.errorRoomJoinFailed + '.';
        case EApiErrorCode.ROOM_AUTHORIZATION_FAILED_SESSION_ENDED:
          return translations.eventHasEnded + '.';
        case EApiErrorCode.ROOM_AUTHORIZATION_FAILED_NOT_LIVE_YET:
          return translations.eventHasNotStarted + '.';
      }
    }

    return translations.requestError;
  }, [error, isLoading, isLoginFailed]);

  const onLogin = useCallback(
    (params?: TOnLoginParams) => {
      return interactionSessionActions.authorizeInteractionUser({ type: loginType, ...params });
    },
    [loginType, interactionSessionActions],
  );

  const onResetError = useCallback(() => {
    interactionSessionActions.resetAuthError();
  }, [interactionSessionActions]);

  return {
    isLoading,
    isLoginFailed,
    isRoomUserAuthAttempted,
    roomInfo,
    log,
    error,
    errorMessage,
    onLogin,
    onResetError,
  };
}
